// Sticky in-page "Jump to" bar for blog posts: one link per h2 section,
// pinned under the site header, highlighting the section being read.
import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { mq } from '../../helpers'

const MIN_NAV_ENTRIES = 1
const STICKY_HEADER_HEIGHT = 60
export const JUMP_NAV_OFFSET = 136

const JumpNavWrapper = styled.nav`
  position: sticky;
  top: var(--jump-nav-top, 0px);
  z-index: 970;
  background-color: ${c.white};
  border-bottom: 1px solid ${c.lightTangerine};
  transition: top 0.25s ease;

  ${mq('max', '768px')} {
    top: 68px;
  }

  .scroller {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 88px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    ${mq('max', '800px')} {
      padding: 10px 16px;
    }
  }

  .label {
    flex-shrink: 0;
    margin-right: 8px;
    color: ${c.cobalt};
    font-family: Euclid;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }

  ul {
    display: flex;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  a {
    display: block;
    padding: 8px 12px;
    color: ${c.black};
    font-family: Euclid;
    font-size: 14px;
    white-space: nowrap;
    text-decoration: none;
    border-radius: 4px;

    &:hover {
      color: ${c.cobalt};
    }

    &[aria-current='location'] {
      background-color: ${c.salmon};
      color: ${c.cobalt};
    }
  }
`

/**
 * Sticky "Jump to" nav for a blog post. Renders nothing when there are fewer
 * than MIN_NAV_ENTRIES entries. The current section is the last heading whose
 * top has scrolled to within JUMP_NAV_OFFSET px of the viewport top; on narrow
 * screens the bar scrolls sideways to keep that link visible. On desktop it
 * pins to the top of the viewport until the site's sticky header slides in,
 * then sits below it, and it moves down by the height of a Klaviyo banner
 * (the padding `useKlaviyoBannerOffset` adds to #sticky-nav).
 *
 * @param {{ entries: Array<{ id: string, label: string, text: string }> }} props
 */
export default function BlogJumpNav({ entries }) {
  const [activeId, setActiveId] = useState(null)
  const [headerShown, setHeaderShown] = useState(false)
  const [bannerOffset, setBannerOffset] = useState(0)
  const scrollerRef = useRef(null)

  useEffect(() => {
    const stickyNav = document.getElementById('sticky-nav')
    const stickyHeader = stickyNav?.querySelector('.sticky-content')
    if (!stickyHeader) {
      return undefined
    }
    const sync = () => {
      setHeaderShown(stickyHeader.classList.contains('show-sticky'))
      setBannerOffset(parseFloat(stickyNav.style.paddingTop) || 0)
    }
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(stickyNav, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      subtree: true
    })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--klaviyo-banner-offset', `${bannerOffset}px`)
    return () => root.style.removeProperty('--klaviyo-banner-offset')
  }, [bannerOffset])

  useEffect(() => {
    if (entries.length < MIN_NAV_ENTRIES) {
      return undefined
    }

    let frame = null
    const update = () => {
      frame = null
      let current = null
      entries.forEach(({ id }) => {
        const heading = document.getElementById(id)
        if (heading && heading.getBoundingClientRect().top <= JUMP_NAV_OFFSET + bannerOffset + 1) {
          current = id
        }
      })
      setActiveId(current)
    }
    const onScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update)
      }
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [entries, bannerOffset])

  useEffect(() => {
    const scroller = scrollerRef.current
    const active = scroller?.querySelector('[aria-current="location"]')
    if (!scroller || !active) {
      return
    }
    const start = active.offsetLeft
    const end = start + active.offsetWidth
    if (start < scroller.scrollLeft || end > scroller.scrollLeft + scroller.clientWidth) {
      scroller.scrollTo?.({ left: Math.max(start - 16, 0), behavior: 'smooth' })
    }
  }, [activeId])

  if (entries.length < MIN_NAV_ENTRIES) {
    return null
  }

  return (
    <JumpNavWrapper
      aria-label='Jump to section'
      style={{ '--jump-nav-top': `${bannerOffset + (headerShown ? STICKY_HEADER_HEIGHT : 0)}px` }}
    >
      <div className='scroller' ref={scrollerRef}>
        <span className='label' aria-hidden='true'>Jump to</span>
        <ul>
          {entries.map(({ id, label, text }) => (
            <li key={id}>
              <a
                href={`#${id}`}
                title={text}
                aria-label={text}
                aria-current={activeId === id ? 'location' : undefined}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </JumpNavWrapper>
  )
}
